import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, Save, X, Upload, Loader2, ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DivisionsManagementPage() {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    head: '',
    description: '',
    full_description: '',
    thumbnail_image: '',
    gallery: [],
    focus_areas: [],
    is_active: true
  });

  const [fileData, setFileData] = useState({});

  // Temporary input for focus areas and gallery
  const [newFocusArea, setNewFocusArea] = useState('');
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  useEffect(() => {
    fetchDivisions();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const uploadFiles = async () => {
    const uploadedUrls = {};
    const uploadPromises = [];

    // Identify files to upload
    for (const [key, files] of Object.entries(fileData)) {
      const fileList = Array.isArray(files) ? files : [files];

      for (const file of fileList) {
        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);

          const promise = axios.post(`${API_URL}/api/files/upload?subfolder=divisions`, formData, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }).then(response => {
            let url = response.data.file_path;
            if (url) {
              if (url.includes('data/files/')) url = url.replace('data/files/', '');
              if (!url.startsWith('/uploads/')) url = '/uploads/' + (url.startsWith('/') ? url.slice(1) : url);
            }
            if (!uploadedUrls[key]) uploadedUrls[key] = [];
            uploadedUrls[key].push(url);
          });

          uploadPromises.push(promise);
        }
      }
    }

    if (uploadPromises.length > 0) {
      toast.info(`Uploading ${uploadPromises.length} files...`);
      await Promise.all(uploadPromises);
    }

    return uploadedUrls;
  };

  const fetchDivisions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/divisions?active_only=false`);
      setDivisions(response.data);
    } catch (error) {
      console.error('Error fetching divisions:', error);
      toast.error('Failed to load divisions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDivision(null);
    setForm({
      name: '',
      head: '',
      description: '',
      full_description: '',
      thumbnail_image: '',
      gallery: [],
      focus_areas: [],
      is_active: true
    });
    setFileData({});
    setOpenDialog(true);
  };

  const handleEdit = (division) => {
    const safeParse = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      return [];
    };

    setEditingDivision(division);
    setForm({
      name: division.name || '',
      head: division.head || '',
      description: division.description || '',
      full_description: division.full_description || '',
      thumbnail_image: division.thumbnail_image || '',
      gallery: safeParse(division.gallery),
      focus_areas: safeParse(division.focus_areas),
      is_active: division.is_active !== false
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Division name is required');
      return;
    }

    try {
      setSaving(true);

      // Upload files first
      const uploadedUrls = await uploadFiles();

      // Update form data with new URLs
      let finalForm = { ...form };

      if (uploadedUrls.thumbnail_image && uploadedUrls.thumbnail_image.length > 0) {
        finalForm.thumbnail_image = uploadedUrls.thumbnail_image[0];
      }

      if (uploadedUrls.gallery && uploadedUrls.gallery.length > 0) {
        finalForm.gallery = [...finalForm.gallery, ...uploadedUrls.gallery];
      }

      if (editingDivision) {
        // Update existing
        await axios.put(
          `${API_URL}/api/divisions/${editingDivision.id}`,
          finalForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Division updated successfully');
      } else {
        // Create new
        await axios.post(
          `${API_URL}/api/divisions`,
          finalForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Division created successfully');
      }

      setFileData({});
      fetchDivisions();
    } catch (error) {
      console.error('Error saving division:', error);
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save division');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (divisionId) => {
    if (!confirm('Are you sure you want to delete this division?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/divisions/${divisionId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Division deleted successfully');
      fetchDivisions();
    } catch (error) {
      console.error('Error deleting division:', error);
      toast.error('Failed to delete division');
    }
  };

  const addFocusArea = () => {
    if (newFocusArea.trim()) {
      setForm(prev => ({
        ...prev,
        focus_areas: [...prev.focus_areas, newFocusArea.trim()]
      }));
      setNewFocusArea('');
    }
  };

  const removeFocusArea = (index) => {
    setForm(prev => ({
      ...prev,
      focus_areas: prev.focus_areas.filter((_, i) => i !== index)
    }));
  };

  const addGalleryImage = () => {
    if (newGalleryUrl.trim()) {
      setForm(prev => ({
        ...prev,
        gallery: [...prev.gallery, newGalleryUrl.trim()]
      }));
      setNewGalleryUrl('');
    }
  };

  const removeGalleryImage = (index) => {
    setForm(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-[#0E6D2B]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Divisions Management</h1>
              <p className="text-sm text-gray-600">Manage research divisions</p>
            </div>
          </div>
          <Button onClick={handleCreate} className="bg-[#0E6D2B] hover:bg-[#0a5a23]">
            <Plus className="w-4 h-4 mr-2" />
            Add Division
          </Button>
        </div>

        {/* Divisions List */}
        <div className="grid gap-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0E6D2B]" />
            </div>
          ) : (
            divisions.map((div) => (
              <Card key={div.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{div.name}</CardTitle>
                      {div.head && <p className="text-sm text-gray-500 mt-1">Head: {div.head}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEdit(div)} size="sm" variant="outline">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleDelete(div.id)} size="sm" variant="destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    {div.thumbnail_image && (
                      <img src={div.thumbnail_image} alt={div.name} className="w-20 h-20 rounded object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 line-clamp-2">{div.description}</p>
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>Gallery: {div.gallery?.length || 0} images</span>
                        <span>Focus Areas: {div.focus_areas?.length || 0}</span>
                        <span className={div.is_active ? 'text-green-600' : 'text-red-600'}>
                          {div.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Edit/Create Dialog */}
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDivision ? 'Edit Division' : 'Add New Division'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Division Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Division of Crop Improvement"
                  />
                </div>
                <div>
                  <Label>Head (Optional)</Label>
                  <Input
                    value={form.head}
                    onChange={(e) => setForm({ ...form, head: e.target.value })}
                    placeholder="Dr. Name"
                  />
                </div>
              </div>

              {/* Thumbnail Image */}
              <div>
                <Label>Thumbnail Image</Label>
                <FileUploadInput
                  field={{ name: 'thumbnail_image', type: 'file', fileType: 'image' }}
                  value={fileData.thumbnail_image}
                  existingUrl={form.thumbnail_image}
                  formData={form}
                  setFormData={setForm}
                  onChange={(files) => setFileData({ ...fileData, thumbnail_image: files })}
                  onDeleteServerFile={() => { setForm({ ...form, thumbnail_image: '' }); return true; }}
                />
              </div>

              {/* Short Description */}
              <div>
                <Label>Short Description (shown on main page)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description for the division card..."
                  rows={3}
                />
              </div>

              {/* Full Description with CKEditor */}
              <div>
                <Label>Full Description (Rich Text - shown in modal)</Label>
                <RichTextEditor
                  value={form.full_description}
                  onChange={(value) => setForm({ ...form, full_description: value })}
                  placeholder="Enter detailed description..."
                />
              </div>

              {/* Gallery Images */}
              <div>
                <Label>Gallery Images</Label>
                <FileUploadInput
                  field={{ name: 'gallery', type: 'file', multiple: true, fileType: 'image' }}
                  value={fileData.gallery}
                  existingUrl={form.gallery}
                  formData={form}
                  setFormData={setForm}
                  onChange={(files) => setFileData({ ...fileData, gallery: files })}
                  onDeleteServerFile={async (path) => {
                    const id = path.split('/').pop();
                    if (id) {
                      // We could also delete from server, but just updating UI is often enough if we don't want to double confirm
                      await axios.delete(`${API_URL}/api/files/${id}`, { headers: getAuthHeaders() });
                      return true;
                    }
                    return false;
                  }}
                />
              </div>

              {/* Focus Areas */}
              <div>
                <Label>Focus Areas</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newFocusArea}
                    onChange={(e) => setNewFocusArea(e.target.value)}
                    placeholder="Focus area"
                    onKeyPress={(e) => e.key === 'Enter' && addFocusArea()}
                  />
                  <Button onClick={addFocusArea} size="sm" type="button">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {form.focus_areas.length > 0 && (
                  <ul className="space-y-1">
                    {form.focus_areas.map((area, index) => (
                      <li key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">{area}</span>
                        <button
                          onClick={() => removeFocusArea(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_active">Active (visible on public page)</Label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-4">
                <Button onClick={() => setOpenDialog(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#0E6D2B] hover:bg-[#0a5a23]">
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Division
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
